import { useState, useCallback } from 'react';
import { MessagesSquare, Loader2 } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { useSdsAssistant } from '../hooks/useSdsAssistant';
import { sdsAssistantApi } from '../services/sdsAssistantApi';
import { SdsChat } from '../components/sds-assistant/SdsChat';
import { SourcesPanel } from '../components/ai/SourcesPanel';

export function SdsAssistantPage() {
    const { canView, isLoading } = usePagePermissions('/sds-assistant');
    const { toast } = useToast();
    const { messages, sources, reasoning, isSending, error, sendMessage, clearChat } = useSdsAssistant();

    // Which source the user last jumped to via an inline [n] marker.
    const [highlightedId, setHighlightedId] = useState<number | null>(null);

    const handleClear = useCallback(() => {
        clearChat();
        setHighlightedId(null);
    }, [clearChat]);

    const handleFeedback = useCallback(async (question: string, answer: string, rating: 1 | -1) => {
        try {
            await sdsAssistantApi.sendFeedback({ question, answer, rating, sources });
            toast('Thanks for the feedback', 'success');
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Feedback could not be saved', 'error');
            throw e; // let the chat roll back its optimistic thumb state
        }
    }, [sources, toast]);

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="mx-auto max-w-md py-20 text-center">
                <h2 className="text-lg font-semibold text-slate-700">No access</h2>
                <p className="mt-2 text-sm text-slate-500">
                    You don't have permission for the SDS Assistant. Please ask an admin to grant
                    access in the settings.
                </p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-full">
            <div className="mb-4 flex items-center gap-2">
                <MessagesSquare className="h-6 w-6 text-emerald-600" />
                <div>
                    <h1 className="text-xl font-semibold text-slate-800">SDS Assistant</h1>
                    <p className="mt-0.5 text-xs text-slate-500">
                        Questions about the SDS/MSDS datasheets — every answer with sources, matching
                        passage and reasoning. Expired datasheets (4_OLD) are excluded.
                    </p>
                </div>
            </div>

            <div
                className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_380px] lg:grid-rows-1"
                style={{ height: 'calc(100vh - 190px)', minHeight: '420px' }}
            >
                <SdsChat
                    messages={messages}
                    isSending={isSending}
                    error={error}
                    onSend={sendMessage}
                    onClear={handleClear}
                    onCitationClick={setHighlightedId}
                    onFeedback={handleFeedback}
                    sourceCount={sources.length}
                />
                <SourcesPanel
                    sources={sources}
                    reasoning={reasoning}
                    highlightedId={highlightedId}
                    isSending={isSending}
                />
            </div>
        </div>
    );
}
